import { useCallback, useEffect, useRef, useState } from "react"

import { CreditApplicationWarning } from "~/components/Tailwindcss/CreditApplicationWarning"
import {
  buildPublicLightReachApplicationBody,
  clearCoApplicant,
  initialLightReachForm,
  lightreachFormComplete,
  setDisclosureAcceptance,
  validateLightReachSsn,
  type LightReachFormData,
  type LightReachPrefill,
} from "~/containers/Proposal/Brochure/FinancialSummarySlide/credit-application-modal/LightReach/lightreach-application"
import { LightReachApplicationForm } from "~/containers/Proposal/Brochure/FinancialSummarySlide/credit-application-modal/LightReach/LightReachApplicationForm"
import { PublicApplicationDialog } from "~/containers/PublicPresentation/PublicQualification/PublicApplicationDialog"

import {
  LIGHTREACH_APPLICATION_TITLE,
  LIGHTREACH_DISCLOSURES_UNAVAILABLE,
} from "./lightreach-application-copy"
import type { LightReachApplicationBundle } from "./lightreach-kind-bundle"

export const LIGHTREACH_SUBMIT_FAILED_MESSAGE = `LightReach could not take the application. Try again.`
export const LIGHTREACH_STALE_OFFER_MESSAGE = `The saved LightReach offer is out of date. Refresh it in Loan Product, then apply.`
const SSN_CORRECTION_MESSAGE = `Please correct the SSN errors before submitting.`

const HOMEOWNER_FACTS: ReadonlyArray<[keyof LightReachFormData, string]> = [
  [`firstName`, `first name`],
  [`lastName`, `last name`],
  [`email`, `email`],
  [`phoneNumber`, `phone number`],
  [`address1`, `street address`],
  [`city`, `city`],
  [`state`, `state`],
  [`zip`, `ZIP code`],
]

/**
 * The applicant's identity is the Proposal's and is read-only here, so a
 * missing fact cannot be typed in. Name it rather than leave Submit dead.
 */
export const missingHomeownerFacts = (form: LightReachFormData): string[] =>
  HOMEOWNER_FACTS.filter(([field]) => {
    const value = form[field]
    return typeof value === `string` && value.trim() === ``
  }).map(([, label]) => label)

/**
 * The seller's LightReach credit application: the shared
 * `LightReachApplicationForm`, prefilled from the Proposal homeowner and
 * site. The gateway reads the applicant's name, contact and address from the
 * Proposal, so those stay read-only and the body carries only the SSN, the
 * disclosures accepted at the version shown, and any co-applicant.
 */
export function LightReachApplication({
  bundle,
  proposalId,
  applicant,
  onClose,
  onSubmitted,
}: {
  bundle: LightReachApplicationBundle
  proposalId: string
  /** The Proposal homeowner and site the form is prefilled from. */
  applicant: LightReachPrefill
  /** Keep stable (`useCallback`): the dialog re-binds when it changes. */
  onClose: () => void
  onSubmitted: () => void
}): JSX.Element {
  const { testIdPrefix } = bundle
  const disclosuresQuery = bundle.useDisclosures(proposalId)
  const submit = bundle.useSubmit()

  const submitErrorMessage = (error: unknown): string =>
    bundle.isRefreshRequiredError(error)
      ? (bundle.staleOfferMessage ?? LIGHTREACH_STALE_OFFER_MESSAGE)
      : (bundle.errorMessage(error) ?? LIGHTREACH_SUBMIT_FAILED_MESSAGE)

  const [formData, setFormData] = useState<LightReachFormData>(() =>
    initialLightReachForm(applicant),
  )
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [ssnError, setSsnError] = useState<string | undefined>(undefined)
  const [coSsnError, setCoSsnError] = useState<string | undefined>(undefined)

  const disclosures = disclosuresQuery.data ?? []

  // An in-flight submission cannot be abandoned: the server is already
  // holding the application. A ref keeps `close` stable, since the dialog
  // re-runs its focus and Escape wiring whenever `onClose` changes.
  const submitting = useRef(false)
  useEffect(() => {
    submitting.current = submit.isLoading
  }, [submit.isLoading])
  const { reset } = submit
  // Detach from the mutation on close; with no observer left the hook's zero
  // cacheTime drops it, SSN and all.
  const close = useCallback((): void => {
    if (submitting.current) {
      return
    }
    reset()
    onClose()
  }, [onClose, reset])

  const handleSubmit = (): void => {
    setSubmitError(null)

    const primarySsnError = validateLightReachSsn(formData.ssn)
    setSsnError(primarySsnError)
    const coApplicantSsnError = formData.showCoApplicant
      ? validateLightReachSsn(formData.coSsn)
      : undefined
    if (formData.showCoApplicant) {
      setCoSsnError(coApplicantSsnError)
    }
    if (primarySsnError || coApplicantSsnError) {
      setSubmitError(SSN_CORRECTION_MESSAGE)
      return
    }

    submit.mutate(
      {
        proposalId,
        body: buildPublicLightReachApplicationBody(formData, disclosures),
      },
      {
        onSuccess: () => {
          reset()
          onSubmitted()
        },
        onError: (error) => setSubmitError(submitErrorMessage(error)),
      },
    )
  }

  const missingFacts = missingHomeownerFacts(formData)

  const disclosuresUnavailable =
    disclosuresQuery.isError ||
    (disclosuresQuery.data != null && disclosures.length === 0)

  return (
    <PublicApplicationDialog
      aboveDrawer
      label={LIGHTREACH_APPLICATION_TITLE}
      onClose={close}
    >
      <div data-testid={`${testIdPrefix}-lightreach-application-form`}>
        {disclosuresUnavailable ? (
          <div className="flex flex-col">
            <p
              role="alert"
              data-testid={`${testIdPrefix}-lightreach-disclosures-unavailable`}
              className="p-6 text-sm text-slate-700"
            >
              {/* A refusal (LightReach no longer offered here) names its
                  own reason, which a retry cannot fix. */}
              {(disclosuresQuery.isError
                ? bundle.errorMessage(disclosuresQuery.error)
                : null) ?? LIGHTREACH_DISCLOSURES_UNAVAILABLE}
            </p>
            <div className="flex justify-end border-t border-gray-200 bg-gray-50 px-6 py-4">
              <button
                type="button"
                onClick={close}
                className="rounded-md border border-gray-300 bg-white px-4 py-2 text-gray-700 transition-colors hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        ) : (
          <LightReachApplicationForm
            formState={formData}
            disclosures={disclosures}
            disclosuresLoading={disclosuresQuery.isLoading}
            ssnError={ssnError}
            coSsnError={coSsnError}
            submitError={submitError}
            submitting={submit.isLoading}
            identityReadOnly
            banner={
              missingFacts.length > 0 ? (
                <CreditApplicationWarning>
                  LightReach needs the homeowner&apos;s{` `}
                  {missingFacts.join(`, `)}. Add{` `}
                  {missingFacts.length === 1 ? `it` : `them`} to the project in
                  PowurOS before applying.
                </CreditApplicationWarning>
              ) : null
            }
            canSubmit={
              lightreachFormComplete(formData, disclosures) && !submit.isLoading
            }
            onFieldChange={(field, value) => {
              setFormData((previous) => ({ ...previous, [field]: value }))
              if (submitError) setSubmitError(null)
              if (field === `ssn` && ssnError) setSsnError(undefined)
              if (field === `coSsn` && coSsnError) setCoSsnError(undefined)
            }}
            onDisclosureChange={(role, disclosureId, accepted) => {
              setFormData((previous) =>
                setDisclosureAcceptance(previous, role, disclosureId, accepted),
              )
              if (submitError) setSubmitError(null)
            }}
            onSsnBlur={() => setSsnError(validateLightReachSsn(formData.ssn))}
            onCoSsnBlur={() =>
              setCoSsnError(validateLightReachSsn(formData.coSsn))
            }
            onAddCoApplicant={() =>
              setFormData((previous) => ({ ...previous, showCoApplicant: true }))
            }
            onRemoveCoApplicant={() => setFormData(clearCoApplicant)}
            onSubmit={handleSubmit}
            onCancel={close}
          />
        )}
      </div>
    </PublicApplicationDialog>
  )
}
